// 1NCE SIM (documentation/onence.md): Settings -> Configuration -> 1NCE SIM, the SIM checks and the
// Device Details SIM actions. Platform administrators only (the server is the boundary). The client
// secret is sent once, write-only, and is never returned; the browser never chooses which ICCID is
// looked up or changed, nor the 1NCE Management API version (one global server setting). A SIM action
// sends only the server's own view back ("expected"), so a changed SIM or setting is refused.
import { ConfigurationError, type LastCheck } from './configuration';

export interface OnenceCredentialStatus {
  available: boolean;
  configured: boolean;
  client_id_hint?: string;
  updated_at?: string;
  error?: string;
}

export type OnenceApiVersion = 'v1' | 'v2';

export interface OnenceView {
  /** not_available | not_configured | configured | connected | verification_failed */
  status: string;
  /** The global Management API version every lookup and the connection test use ('' if unreadable). */
  api_version: OnenceApiVersion | '';
  api_versions?: OnenceApiVersion[];
  api_version_updated_at?: string;
  api_version_updated_by?: string;
  api_version_error?: string;
  v1_sunset_note?: string;
  base_url: string;
  rate_budget_tps: number;
  credential: OnenceCredentialStatus;
  last_check: LastCheck | null;
  portal_url: string;
}

export type OnenceSubjectKind = 'radio' | 'inrico_radio' | 'usb_provisioning';

export interface OnenceCandidate {
  iccid: string;
  /** USB_ADB | USB_ADB_UNMATCHED | SELF_REPORTED */
  source: string;
  /** observed | self_reported */
  trust: string;
  observed_at?: string;
}

export interface OnenceSIM {
  /** 1NCE's iccid: WITHOUT the Luhn check digit. */
  iccid?: string;
  iccid_with_luhn?: string;
  imsi?: string;
  /** As 1NCE reports it: may be a 16-digit IMEISV (displayImei derives the IMEI). */
  imei?: string;
  imei_lock?: boolean;
  status?: string;
  /** The SIM's IP address as 1NCE reports it (ip_address). */
  ip_address?: string;
  label?: string;
  activation_date?: string;
}

export interface OnenceConnectivity {
  /** ONLINE (active data session) | ATTACHED (may be stale) | OFFLINE | BLOCKED (v2) | UNKNOWN | UNRECOGNISED */
  status: string;
  session_ip?: string;
  session_ips?: string[];
  last_updated_at?: string;
  operator?: string;
  country?: string;
  country_iso?: string;
  /** Radio access technology of the single PDP context (2G, 4G, NB-IoT, ...). */
  rat?: string;
  /** v2 only: REACHED | NOT_REACHED */
  data_limit?: string;
}

/** The SIM's data quota in MB. expires_at is the expiry of THIS quota, not of the SIM. */
export interface OnenceQuota {
  remaining_mb?: number;
  total_mb?: number;
  used_mb?: number;
  used_percent?: number;
  /** v2 only: ACTIVE | EXHAUSTED | EXPIRED */
  state?: string;
  expires_at?: string;
}

export interface OnenceCheck {
  name: string;
  /** match | mismatch | not_seen | unavailable */
  result: string;
  detail: string;
}

export type OnenceSimAction = 'disable' | 'enable' | 'imei_lock' | 'imei_unlock' | 'reset';

/** The server's view a SIM action must echo (stale-view check). */
export interface OnenceActionExpectation {
  iccid: string;
  api_version: OnenceApiVersion;
  settings_revision: number;
  sim_status: string;
  imei_lock: boolean | null;
}

export interface OnenceActionRecord {
  action_id: string;
  action: OnenceSimAction;
  api_version?: OnenceApiVersion;
  /** confirmed | pending | queued | rejected | not_sent | uncertain | in_progress | no_outcome */
  state: string;
  code?: string;
  actor?: string;
  started_at: string;
  ended_at?: string;
  /** The state a PUT asked for (absent for a reset). */
  requested_status?: string;
  requested_imei_lock?: boolean;
}

/** Device Details SIM actions. Controls exist only when enabled and not blocked. */
export interface OnenceActionPanel {
  enabled: boolean;
  blocked_code?: string;
  blocked?: string;
  options: { action: OnenceSimAction; available: boolean; reason?: string }[];
  expected?: OnenceActionExpectation;
  last?: OnenceActionRecord;
}

export interface OnenceActionResult {
  action_id: string;
  action: OnenceSimAction;
  api_version: OnenceApiVersion;
  /** confirmed | pending | queued | rejected | not_sent | uncertain */
  outcome: string;
  category?: string;
  provider_status?: number;
  retry_after_seconds?: number;
  after?: { status?: string; imei_lock?: boolean };
  verify_error?: string;
  audit_recorded: boolean;
}

export interface OnenceAssessment {
  configured: boolean;
  /** The Management API version this assessment was made with. */
  api_version?: OnenceApiVersion;
  /** Revision of the version setting this assessment was made under. */
  api_settings_revision?: number;
  subject: { kind: OnenceSubjectKind; subject_id: string; candidates: OnenceCandidate[] };
  iccid?: string;
  iccid_source?: string;
  iccid_trust?: string;
  sim?: OnenceSIM;
  connectivity?: OnenceConnectivity;
  lookup_error?: string;
  status_error?: string;
  quota?: OnenceQuota;
  quota_error?: string;
  retry_after_seconds?: number;
  checks: OnenceCheck[];
  mismatches: string[];
  readiness: { state: string; reasons: string[]; remote_install_available: boolean; remote_install_note: string };
  checked_at: string;
  portal_url: string;
  /** Device Details only. */
  actions?: OnenceActionPanel;
}

/** The documented 1NCE portal root. No per-SIM deep link is published, so none is built. */
export const ONENCE_PORTAL_URL = 'https://portal.1nce.com/';

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, { ...init, headers: init.body ? { 'Content-Type': 'application/json' } : undefined });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new ConfigurationError(body.error || body.message || `Request failed (${response.status})`, body.code, body.issues || []);
  return body as T;
}

const BASE = '/api/admin/configuration/onence';

export const getOnence = () => request<OnenceView>(BASE);
export const saveOnenceCredentials = (clientId: string, clientSecret: string) =>
  request<OnenceView>(`${BASE}/credentials`, { method: 'PUT', body: JSON.stringify({ client_id: clientId, client_secret: clientSecret }) });
export const removeOnenceCredentials = () => request<OnenceView>(`${BASE}/credentials`, { method: 'DELETE' });
/** Connection test: a token, then the smallest read-only SIM API request of the active version. */
export interface OnenceTestResult {
  success: boolean;
  result: string;
  token_result?: string;
  sim_api_result?: string;
  api_version?: OnenceApiVersion;
}
export const testOnence = () => request<OnenceTestResult>(`${BASE}/test`, { method: 'POST' });
export const setOnenceApiVersion = (apiVersion: OnenceApiVersion) =>
  request<OnenceView>(`${BASE}/api-version`, { method: 'PUT', body: JSON.stringify({ api_version: apiVersion }) });

/** Human text of a connection test: which step failed, never "verified" on a token alone. */
export function onenceTestText(r: OnenceTestResult): string {
  const v = r.api_version ? `API ${r.api_version}` : 'API';
  if (r.success) return `1NCE ${v}: token obtained and the SIM API answered a read-only request (token and data discarded)`;
  if (r.token_result && r.token_result !== 'ok') return `1NCE connection test failed: token request - ${onenceErrorText[r.token_result] ?? r.token_result}`;
  const sim = r.sim_api_result ?? r.result;
  if (sim === 'forbidden') {
    return `1NCE ${v}: the credential is valid (token obtained), but the SIM API refused access (403). The SIMs may only be reachable with the other API version.`;
  }
  return `1NCE ${v}: token obtained, but the SIM API request failed - ${onenceErrorText[sim] ?? sim}`;
}

const subjectPath: Record<OnenceSubjectKind, string> = {
  radio: '/api/admin/radios',
  inrico_radio: '/api/admin/inrico/radios',
  usb_provisioning: '/api/admin/usb-provisioning',
};

/** The SIM check of one radio / enrollment / USB record. The server selects the ICCID. */
export const checkOnenceSim = (kind: OnenceSubjectKind, id: string) =>
  request<OnenceAssessment>(`${subjectPath[kind]}/${encodeURIComponent(id)}/onence`);

export class OnenceActionError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message);
  }
}

/** One SIM action on the radio shown in Device Details. The server selects the SIM; expected is the
 *  panel's view, confirm repeats the action the administrator confirmed. Plain fetch: the global fetch
 *  wrapper adds X-CSRF-Token for cookie sessions. A refusal (nothing sent to 1NCE) throws. */
export async function runOnenceSimAction(radioId: string, action: OnenceSimAction, expected: OnenceActionExpectation): Promise<OnenceActionResult> {
  const response = await fetch(`/api/admin/radios/${encodeURIComponent(radioId)}/onence/actions`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, confirm: action, expected }),
  });
  const text = await response.text().catch(() => '');
  let body: Record<string, unknown> = {};
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    body = {};
  }
  if (!response.ok) {
    const message = (typeof body.error === 'string' && body.error) || text.trim() || `Request failed (${response.status})`;
    throw new OnenceActionError(message, response.status, typeof body.code === 'string' ? body.code : undefined);
  }
  return body as unknown as OnenceActionResult;
}

/** The Luhn check digit of a digit string. */
export function luhnDigit(digits: string): number {
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let d = digits.charCodeAt(digits.length - 1 - i) - 48;
    if (i % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return (10 - (sum % 10)) % 10;
}

const luhnValid = (v: string) => /^\d{2,}$/.test(v) && luhnDigit(v.slice(0, -1)) === Number(v[v.length - 1]);

/** The ICCID as 1NCE shows it: a validated 20-digit ICCID without its Luhn check digit (19 digits).
 *  Anything else (masked, other lengths, invalid) is shown unchanged. Display only: requests and
 *  identity checks always use the full ICCID. */
export function displayIccid(full?: string): string {
  if (!full) return '';
  return /^89\d{18}$/.test(full) && luhnValid(full) ? full.slice(0, 19) : full;
}

/** A 15-digit IMEI for display: a 16-digit IMEISV becomes its first 14 digits plus the computed Luhn
 *  check digit (never simply cut); a valid 15-digit IMEI stays; anything else is shown as reported. */
export function displayImei(raw?: string): string {
  if (!raw) return '';
  if (/^\d{16}$/.test(raw)) return raw.slice(0, 14) + String(luhnDigit(raw.slice(0, 14)));
  return raw;
}

/** Human text of the server's fixed failure categories. */
export const onenceErrorText: Record<string, string> = {
  not_configured: '1NCE API credentials are not configured',
  invalid_credentials: '1NCE rejected the API credential',
  forbidden: '1NCE refused access',
  not_found: '1NCE does not know this SIM (not in this customer account)',
  rate_limited: '1NCE rate limit reached - try again later',
  rate_budget_exceeded: 'RadioOS 1NCE request budget reached - try again shortly',
  provider_error: '1NCE service error',
  unexpected_response: 'unexpected answer from 1NCE',
  network_error: '1NCE could not be reached',
  timeout: '1NCE did not answer in time',
  redirect_refused: '1NCE answered with a redirect (refused)',
  invalid_iccid: 'invalid ICCID',
  store_unavailable: 'the credential store is not available on this deployment',
  settings_unavailable: 'the 1NCE API version setting could not be read',
  invalid_api_version: 'invalid 1NCE API version',
  bad_request: '1NCE refused the request as invalid',
  conflict: '1NCE reported a conflicting operation',
  sim_expired: 'the SIM is expired; 1NCE permits only an extension',
};

/** A 1NCE timestamp ("2018-03-09T07:59:09.000+0000", "2023-08-06 09:59:05", ...) for display; the raw
 *  value when it does not parse. */
export function onenceDate(raw?: string, dateOnly = false): string {
  if (!raw) return '';
  const iso = raw.replace(' ', 'T').replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return raw;
  return dateOnly ? d.toLocaleDateString() : d.toLocaleString();
}

/** Megabytes as 1NCE reports them. */
export function formatMB(mb?: number): string {
  if (mb === undefined || mb === null || !Number.isFinite(mb)) return '';
  return `${mb.toLocaleString(undefined, { maximumFractionDigits: mb < 10 ? 2 : 1 })} MB`;
}
